/**
 * Resilient storage adapter for the Supabase auth client.
 *
 * Why this exists: supabase-js reads the session out of localStorage on EVERY
 * request (`getSession()` → `localStorage.getItem`). If that read comes back
 * null the client does not sign the user out — it silently falls back to the
 * anon key. React keeps its `user`, the UI looks logged in, and every write
 * fails RLS (`new row violates row-level security policy`, HTTP 401/400).
 *
 * We saw exactly this on iOS Chrome during onboarding on 19 Sept 2026: the
 * user was authenticated at 03:35:50, opened the photo picker, and from
 * 03:36:06 every request went out as anon with no refresh attempt and no
 * SIGNED_OUT event. WebKit is known to lose the localStorage connection when
 * the page is backgrounded under memory pressure (camera / picker), leaving
 * reads empty until reload.
 *
 * Fix: mirror every auth key in memory. Reads prefer localStorage (so other
 * tabs' sign-outs and token refreshes are honoured) but fall back to the
 * in-memory copy when localStorage has gone blank without telling us. A
 * genuine removal by another tab arrives as a `storage` event, which clears
 * the memory copy too, so we never resurrect a session someone signed out of.
 */

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ResilientAuthStorage extends StorageLike {
  /** Wire to `window.addEventListener('storage', …)`. Exposed for tests. */
  handleStorageEvent(e: { key: string | null; newValue: string | null }): void;
}

interface Options {
  /** Returns the backing store, or null/throws when unavailable (private mode). */
  getLocalStorage?: () => StorageLike | null;
  /** Called the first time a key has to be served from memory. */
  onFallback?: (key: string) => void;
}

function defaultLocalStorage(): StorageLike | null {
  try {
    return typeof globalThis.localStorage === 'object' ? globalThis.localStorage : null;
  } catch {
    // Accessing `localStorage` itself can throw (SecurityError in some sandboxes).
    return null;
  }
}

export function createResilientAuthStorage(options: Options = {}): ResilientAuthStorage {
  const { getLocalStorage = defaultLocalStorage, onFallback } = options;
  const memory = new Map<string, string>();
  const reported = new Set<string>();

  const ls = (): StorageLike | null => {
    try {
      return getLocalStorage();
    } catch {
      return null;
    }
  };

  return {
    getItem(key) {
      let fromStorage: string | null = null;
      try {
        fromStorage = ls()?.getItem(key) ?? null;
      } catch {
        fromStorage = null;
      }
      if (fromStorage !== null) {
        memory.set(key, fromStorage);
        return fromStorage;
      }
      const fromMemory = memory.get(key);
      if (fromMemory === undefined) return null;

      // localStorage lost a value we wrote. Serve the memory copy and report
      // it once. Deliberately NOT written back: another tab may have just
      // signed out and its `storage` event not reached us yet, and a
      // write-back would resurrect that session for every tab. The next token
      // refresh re-persists via setItem anyway.
      if (!reported.has(key)) {
        reported.add(key);
        onFallback?.(key);
      }
      return fromMemory;
    },

    setItem(key, value) {
      memory.set(key, value);
      try {
        ls()?.setItem(key, value);
      } catch {
        // Quota / private mode — memory copy still works for this page.
      }
    },

    removeItem(key) {
      memory.delete(key);
      try {
        ls()?.removeItem(key);
      } catch {
        // Nothing to do; memory copy is already gone.
      }
    },

    handleStorageEvent(e) {
      // `key === null` means clear() was called in another tab.
      if (e.key === null) {
        memory.clear();
        return;
      }
      if (!memory.has(e.key)) return;
      if (e.newValue === null) {
        memory.delete(e.key);
      } else {
        memory.set(e.key, e.newValue);
      }
    },
  };
}
